import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable, of } from 'rxjs'
import { map, switchMap } from 'rxjs/operators'
import { environment } from 'src/environments/environment'

/* ======================================================
 * TYPES
 * ====================================================== */

export type XdrType = 'attack' | 'spam' | 'geoip' | 'dns_watch'
export type XdrSource = 'new' | 'old' | 'mixed'

export interface XdrLog {
  _id: string
  deviceId: string
  type: XdrType
  ts: number
  payload: any
  source: XdrSource
}

export interface MergedXdrResult {
  data: XdrLog[]
  count: number
  source: XdrSource
}

/* ======================================================
 * SERVICE
 * ====================================================== */

@Injectable({ providedIn: 'root' })
export class XdrService {

  constructor(private http: HttpClient) {}

  /* ======================
   * RAW API CALLS
   * ====================== */

  getXdrLogs(params: any) {
    return this.http.get<any>(
      `${environment.endpoints.xdr}/logs`,//old
      { params }
    )
  }

  getXdrDetails(deviceId: string, params: any) {
    return this.http.get<any>(
      `${environment.endpoints.xdr}/${deviceId}/details`,//new
      { params }
    )
  }

  /* ======================
   * MERGED API (NEW + OLD)
   * ====================== */

  getMergedXdrLogs(
    deviceId: string,
    req: {
      page: number
      pageSize: number
      type: XdrType
    }
  ): Observable<MergedXdrResult> {

    const { page, pageSize, type } = req

    // Callers and both APIs use 1-based page numbers.
    const limit = pageSize
    const group = this.mapTypeToGroup(type)

    /* 1️⃣ NEW API FIRST */
    return this.getXdrDetails(deviceId, {
      group,
      page,
      limit
    }).pipe(
      switchMap(newRes => {
        const newLogs: XdrLog[] =
          (newRes?.data ?? []).map((d: any) =>
            this.normalizeNew(d)
          )

        const newCount: number = Number(newRes?.total ?? newLogs.length)

        /* ✅ NEW API FULLY SATISFIES PAGE */
        if (newLogs.length >= limit) {
          return of<MergedXdrResult>({
            data: newLogs.slice(0, limit),
            count: newCount,
            source: 'new'
          })
        }

        /* 2️⃣ TOP-UP FROM OLD API */
        const remaining = limit - newLogs.length

        return this.getXdrLogs({
          deviceId,
          type,
          page,
          pageSize: remaining
        }).pipe(
          map(oldRes => {
            const oldLogs: XdrLog[] =
              (oldRes?.data ?? []).map((d: any) =>
                this.normalizeOld(d, type)
              )

            /* 3️⃣ MERGE + DEDUPE */
            const seen = new Set<string>()
            const merged: XdrLog[] = []

            for (const log of [...newLogs, ...oldLogs]) {
              if (seen.has(log._id)) continue
              seen.add(log._id)
              merged.push(log)
            }

            /* 4️⃣ SORT BY EPOCH DESC */
            merged.sort((a, b) => b.ts - a.ts)

            return {
              data: merged.slice(0, limit),
              count: newCount + Number(oldRes?.count ?? 0),
              source: newLogs.length > 0 ? 'mixed' : 'old'
            } as MergedXdrResult
          })
        )
      })
    )
  }

  /* ======================================================
   * NORMALIZERS
   * ====================================================== */

  private normalizeNew(item: any): XdrLog {
    return {
      _id: item._id,
      deviceId: item.deviceId,
      type: this.mapDetailType(item.type),
      ts: item.ts,
      payload: item.item,
      source: 'new'
    }
  }

  private normalizeOld(item: any, type: XdrType): XdrLog {
    let payload: any = {}

    if (type === 'spam') payload = item.xdrSpam
    if (type === 'attack') payload = item.xdrAttack
    if (type === 'geoip') payload = item.xdrGeoip    
    if (type === 'dns_watch') payload = item.xdrDnsWatch

    return {
      _id: item._id,
      deviceId: item.deviceId,
      type,
      ts: payload?.ts ?? 0,
      payload,
      source: 'old'
    }
  }

  /* ======================================================
   * TYPE MAPPERS
   * ====================================================== */

  private mapDetailType(type: string): XdrType {
    switch (type) {
      case 'Attack_detail':
        return 'attack'

      case 'SPAM_detail':
        return 'spam'

      case 'GeoIP_detail':
        return 'geoip'
      
      case 'DNSWatch_detail':
        return 'dns_watch'

      default:
        throw new Error(`Unknown XDR detail type: ${type}`)
    }
  }

  private mapTypeToGroup(type: XdrType): string {
    switch (type) {
      case 'attack': return 'Attack_detail'
      case 'spam': return 'SPAM_detail'
      case 'geoip': return 'GeoIP_detail'
      case 'dns_watch': return 'DNSWatch_detail'
    }
  }

  /* ======================================================
   * LEGACY (UNCHANGED)
   * ====================================================== */

  getAlertsByFeed(
    deviceId: string,
    feed: string,
    range: string,
    page: number,
    limit: number
  ) {
    const params = new HttpParams()
      .set('range', range)
      .set('page', (page).toString())
      .set('pageSize', limit.toString())

    return this.http.get<any>(
      `${environment.endpoints.alerts}/${feed}/${deviceId}`,
      { params }
    )
  }
}
